// Admin → Integrations → MCP Connectors.
//
// Lists all MCP connectors scoped to the active workspace (from the
// mcp_connectors Supabase table). Supports install (opens MCPConnectorPickerOverlay
// state), disable / re-enable, capability re-discovery, and delete.
//
// Data flow:
//   - `_loadConnectors(root)` pulls mcp_connectors via sdk-bridge/mcp.js.
//   - Results stored in root._mcpConnectors.
//   - MCPConnectorPickerOverlay is mounted at the canvas root — we reuse
//     root._mcpConnectorPickerOpen + root._mcpConnectorDraft state keys.

import {
  listConnectors,
  enableConnector,
  disableConnector,
  deleteConnector,
  discoverCapabilities
} from '../../mcp/index.js'

const _loadConnectors = async (root) => {
  if (!root?.jwtClaims?.workspace_org_id) {
    if (!root._mcpAdminWatcher) {
      root._mcpAdminWatcher = setInterval(() => {
        if (root?.jwtClaims?.workspace_org_id) {
          clearInterval(root._mcpAdminWatcher)
          root._mcpAdminWatcher = null
          _loadConnectors(root)
        }
      }, 300)
    }
    return
  }
  const { data, error } = await listConnectors({})
  root.update({ _mcpConnectors: data || [], _mcpConnectorsError: error || null })
}

const _toast = (el, payload) => {
  try { el.call('openNotification', payload) }
  catch (err) { console.warn('[mcp-admin]', payload.title, err) }
}

// ── Connector row card ────────────────────────────────────────────────────────

export const McpConnectorAdminCard = {
  flow: 'y',
  gap: 'Z',
  padding: 'A',
  round: 'Z1',
  background: 'overlay',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'line',

  Header: {
    flow: 'x',
    align: 'center space-between',

    Left: {
      flow: 'x',
      gap: 'Z',
      align: 'center',
      Name: {
        tag: 'span',
        fontSize: 'A',
        fontWeight: '700',
        color: 'title',
        text: (el, s) => s.name || s.url || ''
      },
      IntegrationScopeBadge: {
        scope_type: (el, s) => s.scope_type,
        scope_id: (el, s) => s.scope_id
      }
    },

    Right: {
      flow: 'x',
      gap: 'Z',
      align: 'center',

      StatusPill: {
        tag: 'span',
        padding: 'V Z',
        round: 'Y',
        fontSize: 'Y1',
        fontWeight: '700',
        background: (el, s) => s.status === 'enabled' ? 'cerulean.1' : 'overlayStrong',
        color: (el, s) => s.status === 'enabled' ? 'cerulean' : 'caption',
        text: (el, s) => s.status === 'enabled' ? 'Live' : 'Disabled'
      },

      DiscoverBtn: {
        tag: 'button',
        type: 'button',
        padding: 'V Z',
        round: 'Y',
        background: 'transparent',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'line',
        color: 'title',
        fontSize: 'Y2',
        cursor: 'pointer',
        text: 'Re-discover',
        title: 'Re-run tools/list + resources/list',
        onClick: async (e, el, s) => {
          const { ok, error } = await discoverCapabilities(s.id)
          if (ok) {
            await _loadConnectors(s.root)
            _toast(el, { title: 'Capabilities refreshed', type: 'success' })
          } else {
            _toast(el, { title: 'Discovery failed', message: error, type: 'error' })
          }
        }
      },

      ToggleBtn: {
        tag: 'button',
        type: 'button',
        padding: 'V Z',
        round: 'Y',
        background: 'transparent',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'line',
        color: 'title',
        fontSize: 'Y2',
        cursor: 'pointer',
        text: (el, s) => s.status === 'enabled' ? 'Disable' : 'Enable',
        onClick: async (e, el, s) => {
          const fn = s.status === 'enabled' ? disableConnector : enableConnector
          const { ok, error } = await fn(s.id)
          if (ok) {
            await _loadConnectors(s.root)
            _toast(el, { title: s.status === 'enabled' ? 'Disabled' : 'Enabled', type: 'success' })
          } else {
            _toast(el, { title: 'Update failed', message: error, type: 'error' })
          }
        }
      },

      DeleteBtn: {
        tag: 'button',
        type: 'button',
        padding: 'V Z',
        round: 'Y',
        background: 'transparent',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'line',
        color: 'red',
        fontSize: 'Y2',
        cursor: 'pointer',
        text: 'Disconnect',
        onClick: async (e, el, s) => {
          if (!window.confirm(`Disconnect "${s.name || s.url}"?`)) return
          const { ok, error } = await deleteConnector(s.id)
          if (ok) {
            await _loadConnectors(s.root)
            _toast(el, { title: 'Disconnected', type: 'success' })
          } else {
            _toast(el, { title: 'Delete failed', message: error, type: 'error' })
          }
        }
      }
    }
  },

  Meta: {
    flow: 'y',
    gap: 'V',

    UrlRow: {
      flow: 'x',
      gap: 'Z',
      fontSize: 'Y2',
      Label: { color: 'caption', text: 'URL', minWidth: '80px' },
      Value: { color: 'title', text: (el, s) => s.url || '' }
    },

    TransportRow: {
      flow: 'x',
      gap: 'Z',
      fontSize: 'Y2',
      Label: { color: 'caption', text: 'Transport', minWidth: '80px' },
      Value: { color: 'title', text: (el, s) => s.transport || 'sse' }
    },

    AuthRow: {
      flow: 'x',
      gap: 'Z',
      fontSize: 'Y2',
      Label: { color: 'caption', text: 'Auth', minWidth: '80px' },
      Value: { color: 'title', text: (el, s) => s.auth_kind || 'none' }
    }
  },

  CapabilitiesSection: {
    flow: 'y',
    gap: 'V',
    hide: (el, s) => {
      const caps = s.capabilities || {}
      return !caps.tools?.length && !caps.resources?.length
    },

    CapLabel: {
      tag: 'span',
      fontSize: 'Y2',
      fontWeight: '700',
      color: 'caption',
      text: 'Capabilities'
    },

    ToolsList: {
      flow: 'x',
      gap: 'V',
      flexWrap: 'wrap',
      hide: (el, s) => !(s.capabilities?.tools?.length),
      childrenAs: 'state',
      children: (el, s) => (s.capabilities?.tools || []).map((t) => ({ name: t.name || t })),
      childExtends: {
        tag: 'span',
        padding: 'V Z',
        round: 'Y',
        fontSize: 'Y1',
        background: 'cerulean.1',
        color: 'cerulean',
        text: (el, s) => s.name
      }
    }
  }
}

// ── Page ──────────────────────────────────────────────────────────────────────

export const AdminMcpConnectors = {
  flow: 'y',
  gap: 'A',
  padding: 'A',

  onRender: (el, s) => _loadConnectors(s.root),

  PageHeader: {
    flow: 'x',
    align: 'center space-between',
    marginBottom: 'V',

    Left: {
      flow: 'y',
      gap: 'V',
      Title: {
        tag: 'h2',
        fontSize: 'B',
        fontWeight: '700',
        color: 'title',
        margin: '0',
        text: 'MCP Connectors'
      },
      Subtitle: {
        tag: 'p',
        fontSize: 'Z2',
        color: 'caption',
        margin: '0',
        text: 'Remote MCP servers available to canvas AI agents as tool providers.'
      }
    },

    AddBtn: {
      tag: 'button',
      type: 'button',
      padding: 'V A',
      round: 'Y',
      background: 'blue',
      color: 'white',
      fontSize: 'Y2',
      fontWeight: '700',
      cursor: 'pointer',
      border: 'none',
      text: '+ New MCP connector',
      onClick: (e, el, s) => {
        s.root?.update({
          _mcpConnectorPickerOpen: true,
          _mcpConnectorDraft: {
            transport: 'sse',
            authKind: 'none',
            scopeType: 'workspace',
            scopeId: null
          }
        })
      }
    }
  },

  // MCP connector picker overlay — auto-extended by key name from the
  // canvas components package (MCPConnectorPickerOverlay).
  MCPConnectorPickerOverlay: {},

  ErrorBanner: {
    hide: (el, s) => !s.root?._mcpConnectorsError,
    padding: 'V A',
    round: 'Y',
    background: 'red.1',
    color: 'red',
    fontSize: 'Z2',
    text: (el, s) => `Could not load connectors: ${s.root?._mcpConnectorsError || ''}`
  },

  EmptyState: {
    hide: (el, s) => !!(s.root?._mcpConnectors || []).length,
    flow: 'y',
    gap: 'A',
    align: 'center center',
    padding: 'E A',
    borderWidth: '1px',
    borderStyle: 'dashed',
    borderColor: 'line',
    round: 'Z1',

    Icon: {
      tag: 'span',
      fontSize: 'D',
      text: '⚡',
      opacity: '0.35'
    },
    EmptyTitle: {
      tag: 'h3',
      fontSize: 'A',
      fontWeight: '700',
      color: 'title',
      margin: '0',
      text: 'No MCP connectors yet'
    },
    EmptyHint: {
      tag: 'p',
      fontSize: 'Z2',
      color: 'caption',
      margin: '0',
      textAlign: 'center',
      text: 'Connect the Symbols MCP server or any other MCP-compatible server to give canvas AI agents access to tools and resources.'
    },
    ConnectSymbols: {
      tag: 'button',
      type: 'button',
      padding: 'V A',
      round: 'Y',
      background: 'cerulean',
      color: 'white',
      fontSize: 'Y2',
      fontWeight: '700',
      cursor: 'pointer',
      border: 'none',
      text: 'Connect Symbols MCP',
      onClick: (e, el, s) => {
        s.root?.update({
          _mcpConnectorPickerOpen: true,
          _mcpConnectorDraft: {
            name: 'Symbols MCP',
            url: 'https://mcp.symbols.app',
            transport: 'sse',
            authKind: 'oauth',
            scopeType: 'workspace',
            scopeId: null
          }
        })
      }
    }
  },

  ConnectorList: {
    flow: 'y',
    gap: 'A',
    childExtends: 'McpConnectorAdminCard',
    childrenAs: 'state',
    children: (el, s) => (s.root?._mcpConnectors || []).map((c) => ({ ...c, key: c.id }))
  }
}

// ── Route export ──────────────────────────────────────────────────────────────

export const adminIntegrationsMcp = {
  extends: 'AppPage',
  metadata: { title: 'MCP Connectors / Integrations / Symbols Workspace' },
  onInit: (el, s) => {
    if (!el.call('shouldRedirectNonAdmin', s)) return
    const root = el.call('findRouteRoot', el)
    if (root) el.router('/', root, {}, { pushState: true })
  },
  onCreate: (el, s) => _loadConnectors(s.root),

  AdminSidebar: {},

  AdminMain: {
    extends: 'AppMain',
    padding: '0',

    Header: {
      extends: 'PageHeader',
      LeftGroup: {
        BackLink: {
          tag: 'button',
          type: 'button',
          padding: '0',
          background: 'transparent',
          border: 'none',
          color: 'caption',
          fontSize: 'Z2',
          cursor: 'pointer',
          text: '← Integrations',
          onClick: (e, el, s) => {
            const root = el.call('findRouteRoot', el)
            if (root) el.router('/admin/integrations', root, {}, { pushState: true })
          }
        },
        Title: {
          tag: 'h1',
          text: 'MCP Connectors',
          fontSize: 'B',
          fontWeight: '700',
          color: 'title',
          margin: '0'
        }
      }
    },

    Body: {
      extends: 'AdminMcpConnectors'
    }
  }
}
